import { ISettings } from '@/shared/interfaces/ISettings';
import Dexie, { Table } from 'dexie';

export interface IOnifyTranslation {
  key: string;
  translation: string;
  locale: string;
}

export interface IOnifyCacheKeys {
  name: string;
  key: string;
}

export interface IOnifySettings {
  key: string;
  value: ISettings;
}

/**
 * A class that extends Dexie to manage specific tables for translations and cache keys.
 *
 * @class
 * @name HelixDexie
 * @extends Dexie
 * @description
 * This class initializes a Dexie database named 'onify' with two tables:
 * - `translations`: Stores translation data with a compound primary key of `[key+locale]` and an index on `locale`.
 * - `cacheKeys`: Stores cache key data with a primary key of `name`.
 *
 * @property {Table<IOnifyTranslation>} translations - The table for storing translation data.
 * @property {Table<IOnifyCacheKeys>} cacheKeys - The table for storing cache key data.
 *
 * @constructor
 * @returns {HelixDexie} An instance of HelixDexie.
 */
export class HelixDexie extends Dexie {
  translations!: Table<IOnifyTranslation>;
  settings!: Table<IOnifySettings>;
  cacheKeys!: Table<IOnifyCacheKeys>;

  constructor() {
    super('onify');

    this.version(3.0).stores({
      translations: '[key+locale], locale',
      settings: 'key',
      cacheKeys: 'name',
    });
  }
}

const hWebDb = new HelixDexie();
hWebDb.open();

/**
 * Clears the IndexedDB settings, translations, and cacheKeys tables.
 * Failures are logged and swallowed so logout / tenant switches are never blocked.
 */
export async function clearHelixCache(): Promise<void> {
  try {
    await Promise.all([hWebDb.settings.clear(), hWebDb.translations.clear(), hWebDb.cacheKeys.clear()]);
  } catch (error) {
    console.warn('[cache] Failed to clear Helix IndexedDB cache:', error);
  }
}

export { hWebDb };
